import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';

export interface PositionProps {
  top?: string | number;
  left?: string | number;
  right?: string | number;
  bottom?: string | number;
}

export interface PositionedProps extends PositionProps {
  rotate?: number;
  zIndex?: number;
}

export interface FourPointStarProps extends PositionedProps {
  size?: number;
  variant?: 'filled' | 'outlined';
}

export interface CloudShapeProps extends PositionedProps {
  width?: number;
  height?: number;
}

export interface ElevatedNodeProps {
  children: React.ReactNode;
  size?: number;
  bgcolor?: string;
  elevation?: number;
}

export const getPositionStyles = ({
  top,
  left,
  right,
  bottom,
  rotate = 0,
  zIndex = 1,
}: PositionedProps) => ({
  position: 'absolute' as const,
  top,
  left,
  right,
  bottom,
  transform: `rotate(${rotate}deg)`,
  zIndex,
});

export const FourPointStar = ({
  size = 24,
  top,
  left,
  right,
  bottom,
  rotate = 0,
  variant = 'filled',
}: FourPointStarProps) => {
  const theme = useTheme();
  const isOutlined = variant === 'outlined';
  const iconColor = theme.palette.semantic.icon.primary;

  return (
    <Box
      sx={{
        ...getPositionStyles({
          top,
          left,
          right,
          bottom,
          rotate,
        }),
        width: size,
        height: size,
      }}
    >
      <svg viewBox="-2 -2 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 0C12 6.627 17.373 12 24 12C17.373 12 12 17.373 12 24C12 17.373 6.627 12 0 12C6.627 12 12 6.627 12 0Z"
          fill={isOutlined ? 'none' : iconColor}
          stroke={isOutlined ? iconColor : 'none'}
          strokeWidth={isOutlined ? '2' : '0'}
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
};

export const CloudShape = ({
  top,
  left,
  right,
  bottom,
  rotate = 0,
  width = 478,
  height = 271,
}: CloudShapeProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        ...getPositionStyles({
          top,
          left,
          right,
          bottom,
          rotate,
        }),
        width,
        height,
      }}
    >
      <svg
        width={width}
        height={height}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          opacity="0.12"
          d="M414.921 146.795C409.696 146.795 404.624 147.432 399.768 148.611C400.878 143.868 401.44 139.017 401.443 134.15C401.443 98.559 372.137 69.7061 335.986 69.7061C322.79 69.6879 309.899 73.6159 299.02 80.97C286.741 34.3919 243.751 0 192.6 0C131.882 0 82.6588 48.4612 82.6588 108.24C37.0081 108.24 0 144.675 0 189.62C0 234.564 37.0072 271 82.6588 271H414.921C449.759 271 478 243.196 478 208.897C478 174.599 449.759 146.795 414.921 146.795Z"
          fill={theme.palette.colors.primary[500]}
        />
      </svg>
    </Box>
  );
};

export const CloudSmallShape = ({
  top,
  left,
  right,
  bottom,
  rotate = 0,
  width = 180,
  height = 118,
}: CloudShapeProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        ...getPositionStyles({
          top,
          left,
          right,
          bottom,
          rotate,
        }),
        width,
        height,
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 180 118"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          opacity="0.12"
          d="M90 0C121.691 0 139.669 20.6285 142.279 45.548H143.066C163.474 45.548 180 61.7676 180 81.774C180 101.78 163.463 118 143.078 118H36.9225C16.5263 118 0 101.78 0 81.774C0 61.7676 16.5375 45.548 36.9225 45.548H37.7213C40.3538 20.4676 58.3088 0 90 0Z"
          fill={theme.palette.colors.primary[500]}
        />
      </svg>
    </Box>
  );
};

export const ElevatedNode = ({
  children,
  size = 52,
  bgcolor,
  elevation = 2,
}: ElevatedNodeProps) => (
  <Paper
    elevation={elevation}
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      zIndex: 2,
      bgcolor,
    }}
  >
    {children}
  </Paper>
);

export const DatabaseNode = ({ size }: { size: number }) => (
  <ElevatedNode size={size} bgcolor="white" elevation={2}>
    <svg
      width="26"
      height="24"
      viewBox="0 0 26 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.73017 0C2.00608 0 1.31165 0.287642 0.799648 0.799648C0.287642 1.31165 0 2.00608 0 2.73017L0 8.054C0 8.77808 0.287642 9.47251 0.799648 9.98452C1.31165 10.4965 2.00608 10.7842 2.73017 10.7842H22.7514C23.4755 10.7842 24.1699 10.4965 24.6819 9.98452C25.1939 9.47251 25.4816 8.77808 25.4816 8.054V2.73017C25.4816 2.00608 25.1939 1.31165 24.6819 0.799648C24.1699 0.287642 23.4755 0 22.7514 0H2.73017ZM0 15.946C0 15.2219 0.287642 14.5275 0.799648 14.0155C1.31165 13.5035 2.00608 13.2158 2.73017 13.2158H22.7514C23.4755 13.2158 24.1699 13.5035 24.6819 14.0155C25.1939 14.5275 25.4816 15.2219 25.4816 15.946V21.2698C25.4816 21.9939 25.1939 22.6883 24.6819 23.2003C24.1699 23.7124 23.4755 24 22.7514 24H2.73017C2.00608 24 1.31165 23.7124 0.799648 23.2003C0.287642 22.6883 0 21.9939 0 21.2698L0 15.946Z"
        fill="#8FBFFA"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.77163 3.34521C5.50273 3.34521 5.23646 3.39818 4.98803 3.50108C4.7396 3.60398 4.51387 3.75481 4.32373 3.94495C4.1336 4.13509 3.98277 4.36082 3.87987 4.60925C3.77696 4.85768 3.724 5.12394 3.724 5.39284C3.724 5.66174 3.77696 5.928 3.87987 6.17643C3.98277 6.42486 4.1336 6.65059 4.32373 6.84073C4.51387 7.03087 4.7396 7.1817 4.98803 7.2846C5.23646 7.3875 5.50273 7.44047 5.77163 7.44047C6.31469 7.44047 6.83551 7.22474 7.21952 6.84073C7.60352 6.45673 7.81925 5.9359 7.81925 5.39284C7.81925 4.84978 7.60352 4.32896 7.21952 3.94495C6.83551 3.56095 6.31469 3.34521 5.77163 3.34521ZM3.724 18.6069C3.724 18.338 3.77696 18.0717 3.87987 17.8233C3.98277 17.5748 4.1336 17.3491 4.32373 17.159C4.51387 16.9688 4.7396 16.818 4.98803 16.7151C5.23646 16.6122 5.50273 16.5592 5.77163 16.5592C6.04052 16.5592 6.30679 16.6122 6.55522 16.7151C6.80365 16.818 7.02938 16.9688 7.21952 17.159C7.40965 17.3491 7.56048 17.5748 7.66338 17.8233C7.76629 18.0717 7.81925 18.338 7.81925 18.6069C7.81925 19.1499 7.60352 19.6707 7.21952 20.0547C6.83551 20.4387 6.31469 20.6545 5.77163 20.6545C5.22856 20.6545 4.70774 20.4387 4.32373 20.0547C3.93973 19.6707 3.724 19.1499 3.724 18.6069ZM12.5133 5.39284C12.5133 4.76308 13.023 4.25527 13.6509 4.25527H20.0213C20.323 4.25527 20.6123 4.37512 20.8257 4.58846C21.039 4.80179 21.1589 5.09114 21.1589 5.39284C21.1589 5.69454 21.039 5.98389 20.8257 6.19722C20.6123 6.41056 20.323 6.53041 20.0213 6.53041H13.6509C13.3492 6.53041 13.0598 6.41056 12.8465 6.19722C12.6332 5.98389 12.5133 5.69454 12.5133 5.39284ZM13.6509 17.4693C13.3492 17.4693 13.0598 17.5891 12.8465 17.8025C12.6332 18.0158 12.5133 18.3052 12.5133 18.6069C12.5133 18.9086 12.6332 19.1979 12.8465 19.4112C13.0598 19.6246 13.3492 19.7444 13.6509 19.7444H20.0213C20.323 19.7444 20.6123 19.6246 20.8257 19.4112C21.039 19.1979 21.1589 18.9086 21.1589 18.6069C21.1589 18.3052 21.039 18.0158 20.8257 17.8025C20.6123 17.5891 20.323 17.4693 20.0213 17.4693H13.6509Z"
        fill="#2859C5"
      />
    </svg>
  </ElevatedNode>
);

export const CodeNode = ({ size }: { size: number }) => {
  const theme = useTheme();

  return (
    <ElevatedNode
      size={size}
      bgcolor={theme.palette.colors.white}
      elevation={2}
    >
      <ElevatedNode
        size={size - 5}
        bgcolor={theme.palette.semantic.icon.active}
        elevation={2}
      >
        <svg
          width="27"
          height="24"
          viewBox="0 0 27 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16.9099 0.0587747C16.1131 -0.170864 15.2834 0.2931 15.0538 1.08981L9.05429 22.0854C8.82462 22.8821 9.28865 23.7116 10.0855 23.9412C10.8823 24.1709 11.7119 23.7069 11.9415 22.9102L17.941 1.91463C18.1707 1.11793 17.7067 0.288414 16.9099 0.0587747ZM19.9377 6.43711C19.3518 7.02293 19.3518 7.97429 19.9377 8.5601L23.378 12L19.9377 15.4399C19.3518 16.0257 19.3518 16.9771 19.9377 17.5629C20.5236 18.1487 21.4751 18.1487 22.061 17.5629L26.5606 13.0638C27.1465 12.478 27.1465 11.5267 26.5606 10.9408L22.061 6.4418C21.4751 5.85599 20.5236 5.85599 19.9377 6.4418V6.43711ZM7.06228 6.43711C6.47639 5.8513 5.52491 5.8513 4.93902 6.43711L0.439415 10.9362C-0.146472 11.522 -0.146472 12.4733 0.439415 13.0592L4.93902 17.5582C5.52491 18.144 6.47639 18.144 7.06228 17.5582C7.64816 16.9724 7.64816 16.021 7.06228 15.4352L3.62195 12L7.05759 8.5601C7.64348 7.97429 7.64348 7.02293 7.05759 6.43711H7.06228Z"
            fill="white"
          />
        </svg>
      </ElevatedNode>
    </ElevatedNode>
  );
};

export const ConnectionPath = () => {
  const theme = useTheme();

  return (
    <svg
      viewBox="0 0 565 302"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 0,
      }}
    >
      <path
        d="M111 168 L207 101 L280 176 L371 107"
        stroke={theme.palette.colors.info[400]}
        strokeWidth="2.5"
        strokeDasharray="6 6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
